import { date_dmy_func } from "../../components/Common/CommonFunction";
import { numberWithCommas } from "../Report_common_function";

/**
 * OrderTableData.js — RA Masale format (Purchase/Sales Order print)
 * ------------------------------------------------------------------
 * Same split as the Invoice TableData.js: pure data functions only
 * (no doc.* drawing calls). OrderReportStyle.js turns these into
 * doc.text / autoTable calls.
 *
 * Adapted from the Invoice version to match the Order API shape:
 *   - items live at data.OrderItem (not data.InvoiceItems)
 *   - no BatchCode on order items -> BATCH NO column dropped
 *   - no BaseRate (list price) on order items -> P.LIST column dropped
 *   - discount comes from item.Discount / item.DiscountType
 *     (not item.CustomerMargin)
 *   - "Amount" per line = item.BasicAmount (pre-tax), same convention
 *     as the invoice table
 * ------------------------------------------------------------------
 */

export const columns = [
    "S.N",                    // 0
    "Description of Goods",   // 1
    "HSN",                    // 2
    "MRP",                    // 3
    "Qty",                    // 4
    "Unit",                   // 5
    "Disc",                   // 6
    "Gst %",                  // 7
    "Rate",                   // 8
    "Amount",                 // 9
];

// Column widths in points. MUST sum to <= (page width - left/right
// margins) = 595 - 30 - 30 = 535. Kept at 535 exactly.
export const columnWidths = {
    0: 25,   // S.N
    1: 165,  // Description of Goods
    2: 45,   // HSN
    3: 45,   // MRP
    4: 35,   // Qty
    5: 40,   // Unit
    6: 35,   // Disc
    7: 35,   // Gst %
    8: 50,   // Rate
    9: 65,   // Amount
};

/**
 * Order items don't carry the stacked SubGroupName/GroupName suffix
 * that invoice items sometimes do, so this is a lighter cleanup:
 * just guard against a trailing "/-something" pattern like the
 * invoice's ItemName formatter does.
 */
const cleanItemName = (item) => {
    const name = item.ItemName || "";
    return name.replace(/\/-.*/, "/-");
};

const formatAmount = (value) => {
    const num = Number(value);

    return Number.isFinite(num)
        ? numberWithCommas(num.toFixed(2))
        : "";
};

export const Rows = (data) => {
    const items = data.OrderItem || [];

    let totalQty = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;

    const returnArr = items.map((item, idx) => {
        totalQty += Number(item.Quantity) || 0;
        totalCGST += Number(item.CGST) || 0;
        totalSGST += Number(item.SGST) || 0;
        totalIGST += Number(item.IGST) || 0;

        const discValue = Number(item.Discount) || 0;
        const discDisplay = item.DiscountType === "1" ? `${discValue} Rs` : `${discValue}%`;

        return [
            idx + 1,
            cleanItemName(item),
            item.HSNCode || "",
            formatAmount(item.MRPValue),
            `${parseFloat(item.Quantity) || 0}`,
            item.PrimaryUnitName || "",
            discDisplay,
            `${Number(item.GSTPercentage) || 0}%`,
            formatAmount(item.Rate),
            formatAmount(item.BasicAmount),
        ];
    });

    data["TotalItemlength"] = items.length;

    data["footerTotals"] = {
        totalQty,
        totalCGST,
        totalSGST,
        totalIGST,
        isIGST: totalIGST > 0 && totalCGST === 0 && totalSGST === 0,
    };

    return returnArr;
};

/**
 * GST state code is the first 2 digits of any GSTIN. The order
 * payload has no CustomerState field (unlike the invoice), so we
 * can only show the numeric state code, not the state name.
 */
export const stateCodeFromGSTIN = (gstin) => {
    if (!gstin || gstin.length < 2) return "";
    return gstin.substring(0, 2);
};

/**
 * "Party Details :" box — the CUSTOMER (buyer) on the order, same
 * labelling convention as the invoice. Order's BillingAddress is a
 * plain string (not an array of address objects like the invoice's
 * CustomerAddress), so no filtering-for-default step is needed.
 */
export const PartyDetails = (data) => {
    const gstinLine = [
        `GSTIN/UIN: ${data.CustomerGSTIN || ""}`,
        data.BillingFssai ? `FSSAI No: ${data.BillingFssai}` : "",
    ].filter(Boolean).join("     ");

    return {
        name: data.CustomerName || "",
        address: data.BillingAddress || "",
        gstinLine,
    };
};

/**
 * Right-hand order info block. Each entry is {label, value}.
 * Built from fields that actually exist on the Order API response
 * (PO number, SO number, order/delivery dates, PO type, advance
 * amount) rather than reusing invoice-only fields like Transport,
 * Vehicle No, or E-Way Bill No, which don't exist here.
 */
export const OrderInfoRows = (data) => {
    const stateCode = stateCodeFromGSTIN(data.CustomerGSTIN);
    const deliveryDate = data.DeliveryDate ? data.DeliveryDate.split("T")[0] : "";

    return [
        { label: "Order No", value: data.FullOrderNumber || "" },
        { label: "PO Number", value: data.PONumberFull || "" },
        { label: "SO Number", value: data.SONumberFull || "" },
        { label: "Order Date", value: date_dmy_func ? date_dmy_func(data.OrderDate) : data.OrderDate || "" },
        { label: "Delivery Date", value: deliveryDate ? (date_dmy_func ? date_dmy_func(deliveryDate) : deliveryDate) : "" },
        { label: "Order Type", value: data.POTypeName || "" },
        { label: "GSTIN State Code", value: stateCode },
        { label: "Advance Amount", value: numberWithCommas(Number(data.AdvanceAmount || 0).toFixed(2)) },
    ];
};